import { rememberNativeUid } from '~/utils/nativePersistence'
import { IS_NATIVE } from '~/utils/platform'
import { ACCOUNT_DELETION_SESSION } from '~/utils/accountDeletion'

/**
 * Client-side Firebase bootstrap (v1.16): keeps useAuthUser in sync and
 * reports leaderboard progress at the moments that matter — defeat,
 * victory, an act boss falling, and giving up / starting over (the run
 * ref dropping while progress existed).
 */
/** ⚡ v6.62: nothing here touches Firebase until bootFirebase() runs -
 * at once for a device that has seen a Google sign-in (the account chip
 * must show the name immediately, as it always did), otherwise on the
 * first gesture or eight seconds in, whichever comes first. Everything
 * downstream awaits firebaseReady(), so a call made before the boot
 * simply waits for it. Lighthouse never taps, so the audit sees the
 * title without the SDK, reCAPTCHA or the auth iframe. */
const SIGNED_IN_KEY = 'elemental-dispatch-signed-in'
const BOOT_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const
const BOOT_AFTER_MS = 8000

function startFirebase() {
  const user = useAuthUser()
  const run = useRunState()

  void firebaseReady().then((fb) => {
    fb.authMod.onAuthStateChanged(fb.auth, (u) => {
      user.value = u
        ? { uid: u.uid, name: u.displayName ?? 'Anonymous', photo: u.photoURL ?? null, anonymous: u.isAnonymous }
        : null
      if (u) void rememberNativeUid(u.uid).catch(error => console.warn('Native identity recovery marker unavailable.', error))
      // Clear prior-account talents synchronously; late HTTP responses are ignored.
      useStarweave().onIdentityChanged(u?.uid ?? null)
      try {
        if (u && !u.isAnonymous) localStorage.setItem(SIGNED_IN_KEY, '1')
        else localStorage.removeItem(SIGNED_IN_KEY)
      } catch { /* no storage */ }
      // 👤 v6.43: a real account has (or is asked to pick) an alias.
      if (u && !u.isAnonymous) void useProfile().afterSignIn()
      else useProfile().onSignedOut()
    })
  })

  // ☁️ Meta progress follows the account across devices (v1.17). Both
  // watch the user, which stays null until the boot.
  initCloudSync()
  initRunSync()

  watch(
    () => ({
      defeated: run.value?.defeated,
      won: run.value?.bossDefeated,
      actDone: run.value?.actSummary,
    }),
    (v) => {
      if (v.defeated || v.won || v.actDone !== null && v.actDone !== undefined) {
        void submitProgress(run.value)
      }
    },
  )
  // Abandoning (New Run / Give Up) nulls the ref — submit the final
  // snapshot of the run that just ended.
  // 🏆 v6.38: unless the SYNC layer nulled it. A tombstone adopted from
  // the other device (v6.37) is THAT device's ending, and it already
  // submitted the real final run; what this device holds is a stale
  // copy of it. beatsStored bounds the harm to an equal-score row
  // taking the shorter playtime, but the board should not hear from a
  // device that never played the ending.
  watch(run, (val, old) => {
    if (!val && old && !adoptingFromCloud()) void submitProgress(old)
  })
  // ⏱️ v5.23: a NEW runId means a fresh run was born — stamp its start
  // on the server so the end-of-run submit can clamp claimed play-time
  // to real wall-clock. One active run per account, so the per-account
  // stamp always belongs to the run that can still submit.
  watch(() => run.value?.runId, (id, old) => {
    if (id && id !== old) void registerRunStart(id)
  })

  // The boot policy.
  let seen = false
  try { seen = localStorage.getItem(SIGNED_IN_KEY) === '1' } catch { /* no storage */ }
  if (seen) {
    void bootFirebase()
    return
  }
  let timer: ReturnType<typeof setTimeout> | null = null
  const boot = () => {
    for (const ev of BOOT_EVENTS) window.removeEventListener(ev, boot)
    if (timer) clearTimeout(timer)
    void bootFirebase()
  }
  for (const ev of BOOT_EVENTS) window.addEventListener(ev, boot, { once: true, passive: true })
  timer = setTimeout(boot, BOOT_AFTER_MS)
}

export default defineNuxtPlugin({
  dependsOn: ['native-client-config'],
  setup(nuxtApp) {
    // The deletion form owns auth on its isolated page; no game sync, lazy
    // guest wallet, leaderboard writes or profile prompts may run there.
    if (ACCOUNT_DELETION_SESSION) return
    const { floor } = useNativeUpdate()
    if (!IS_NATIVE || !floor.value) {
      startFirebase()
      return
    }
    // Neither the signed-in fast path nor gestures/the timer can boot the SDK
    // on the update screen. A successful (or fail-open) retry starts it once.
    const stop = watch(floor, (required) => {
      if (required) return
      stop()
      nuxtApp.runWithContext(startFirebase)
    })
  },
})
